import { Controller } from "@hotwired/stimulus"

// A silent loop laid over a still (the still is the poster and the fallback).
// Nothing is downloaded until the clip is near the screen, it plays only while
// visible, and it never loads for people who asked for less motion or less
// data — they keep the still. The pause button covers WCAG 2.2.2.
//
// Usage:
//   <div data-controller="ambient-video">
//     <img …>                                    ← the still, always rendered
//     <video data-ambient-video-target="video" data-src="…" muted loop playsinline preload="none"></video>
//     <button data-ambient-video-target="toggle" data-action="ambient-video#toggle" hidden>…</button>
//   </div>
//
// The wrapper gets .is-playing once frames are showing, which fades the video in.
export default class extends Controller {
  static targets = ["video", "toggle"]

  connect() {
    if (!this.hasVideoTarget || !this.allowed()) return

    this.paused = false
    this.video  = this.videoTarget
    this.video.muted = true // autoplay policies need the property, not just the attribute

    this.onPlaying = () => this.element.classList.add("is-playing")
    this.video.addEventListener("playing", this.onPlaying)

    // Load a screen ahead so it is ready by the time it scrolls in.
    this.loader = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this.load()
        this.loader.disconnect()
      }
    }, { rootMargin: "100% 0px" })
    this.loader.observe(this.element)

    this.watcher = new IntersectionObserver((entries) => {
      this.visible = entries[entries.length - 1].isIntersecting
      this.sync()
    }, { threshold: 0.2 })
    this.watcher.observe(this.element)
  }

  disconnect() {
    this.loader?.disconnect()
    this.watcher?.disconnect()
    this.video?.removeEventListener("playing", this.onPlaying)
  }

  toggle() {
    this.paused = !this.paused
    this.sync()
  }

  // private

  allowed() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false
    if (navigator.connection?.saveData) return false
    return true
  }

  load() {
    if (this.loaded) return
    this.loaded = true
    this.video.src = this.video.dataset.src
    this.video.load()
    if (this.hasToggleTarget) this.toggleTarget.hidden = false
    this.sync()
  }

  sync() {
    if (!this.loaded) return
    if (this.visible && !this.paused) {
      this.video.play().catch(() => {}) // blocked autoplay just leaves the still
    } else {
      this.video.pause()
    }
    this.element.classList.toggle("is-paused", this.paused)
    if (this.hasToggleTarget) {
      this.toggleTarget.setAttribute("aria-label", this.paused ? "Play video" : "Pause video")
    }
  }
}
